/**
 * THE PIECE OF PAPER THE CUSTOMER ACTUALLY GETS.
 *
 * lib/quote.ts is the arithmetic on what somebody typed. This file is
 * everything else a real estimate carries: a number, a date, who it is for,
 * the seasonal price ladder, the terms, and later a deposit. Kept apart on
 * purpose, because the line items are the same whether the header says QUOTE
 * or INVOICE, and a conversion that rewrote the pricing would be a different
 * document wearing the same total.
 *
 * ONE DOCUMENT, TWO STATES. A quote becomes an invoice by changing a word and
 * taking a deposit. It is not copied, renumbered or re-entered. Three weeks
 * later the only question anybody asks is "what did we quote the Ridgeline job
 * and did they pay the deposit", and that question has one answer only if
 * there is one record.
 *
 * THE SEASONAL LADDER IS THE SALE. Book in September and it is fifteen percent
 * off; book in November and it is full price. Every line shows both numbers,
 * struck through and not, because the discount is the argument for signing
 * today. So the ladder is not a footnote here, it is the price.
 *
 * WHY THE LINES ARE AUTHORITATIVE. The discounted line prices are computed
 * first and the total is their sum, and the discount is the difference between
 * that sum and the list. Do it the other way, taking a percentage off the
 * total, and a customer who adds up the right-hand column gets a number that
 * is not the number in the black box. Nobody trusts a quote twice after that.
 */

import type { QuoteLines } from './quote'
import { lineTotal } from './quote'

export type PaperKind = 'quote' | 'invoice'

/**
 * A deposit is nothing, a percentage of the job, or a figure somebody named.
 *
 * NONE IS THE DEFAULT AND THAT IS DELIBERATE. Plenty of shops in this trade
 * take nothing up front, and a form that opens at fifty percent quietly puts a
 * demand on a quote that the person writing it never made. What the contractor
 * chose last time is remembered instead, so the shop that does take half sets
 * it once. See rememberedDeposit below.
 */
export type DepositMode = 'none' | 'percent' | 'flat'

/**
 * MONEY THAT ACTUALLY ARRIVED.
 *
 * Not a request and not a promise. Payments in this trade are handed over
 * outside the app - a check on the kitchen table, a Zelle from the truck, a
 * card run on somebody else's terminal - so recording one is bookkeeping,
 * never collection. Nothing here charges anybody.
 *
 * It is a LIST because part payments are ordinary: a deposit in September, the
 * balance in December, and sometimes a hundred short that turns up in January.
 * A single paid flag cannot say that, and it was what the old deposit.paidAt
 * was being asked to do.
 */
export interface Payment {
  id: string
  /** Dollars received. */
  amount: number
  /** ISO yyyy-mm-dd, the day it arrived. */
  date: string
  /** Card, check, Zelle, cash. Free text, because every shop is different. */
  method: string
  /** Anything worth remembering about it. Usually blank. */
  note: string
}

/**
 * WHEN THE REST IS DUE, and it is not the same question as the deposit.
 *
 * Most of this work is billed on completion. Some of it is billed on a date
 * the contractor names. Those are the only two answers, and collapsing them
 * into the deposit's own timing - which is what a single due date on the paper
 * would do - is how an invoice ends up telling a homeowner the whole balance
 * was due the day they got it.
 */
export type DueOn = 'completion' | 'date'

export interface Deposit {
  mode: DepositMode
  /** Percent when mode is percent, dollars when it is flat. Ignored for none. */
  value: number
  /**
   * LEGACY. The single date a deposit was marked as taken, before payments
   * were a list. Read only by the normalizer below, which turns it into the
   * first Payment and then leaves it alone. Nothing new should write it: a
   * request is not a receipt, and this field could not tell them apart.
   */
  paidAt: string | null
  /** LEGACY, alongside paidAt. */
  method: string
}

/** One rung of the seasonal ladder: install this month, pay this much less. */
export interface Tier {
  /** 0-11, so it sorts and formats without a lookup table. */
  month: number
  offPct: number
}

export interface Paper {
  kind: PaperKind
  /** The bare number. The prefix comes from the company, so GHL + 1068. */
  number: string
  /** ISO yyyy-mm-dd, the day it was written. */
  date: string
  /** Which rung this document is priced at. Null means list price, no ladder. */
  installMonth: number | null
  /**
   * THE LADDER. Any number of rungs, any months, any percentages, or none.
   *
   * Most shops in this trade discount early bookings, but not all of them do
   * and the ones that do not run the same three months. So this is a list a
   * person edits, not a September-October-November constant with the trade's
   * habit baked into it.
   */
  tiers: Tier[]
  /**
   * Whether to print the rungs along the bottom of the page. A shop can take
   * the discount without publishing the ladder: the number comes off either
   * way, and only the argument for signing today disappears.
   */
  showLadder: boolean
  /**
   * What the discount is called on the page. Blank names it after the month,
   * which is what it is nine times out of ten and never what it is the tenth.
   */
  discountLabel: string
  deposit: Deposit
  /** Completion, or a date the contractor names. See DueOn. */
  dueOn: DueOn
  /** ISO yyyy-mm-dd. Only meaningful, and only required, when dueOn is date. */
  dueDate: string
  /** Money actually received, oldest first. Requesting never writes here. */
  payments: Payment[]
  /** The two paragraphs beside the price, in the contractor's own words. */
  termsLeft: string
  termsRight: string
  /** The customer's phone. The document record has a name and a street only. */
  customerPhone: string
}

const THIS_MONTH = () => new Date().getMonth()

export const defaultPaper = (): Paper => ({
  kind: 'quote',
  number: '',
  date: new Date().toISOString().slice(0, 10),
  installMonth: THIS_MONTH(),
  // The ladder every holiday lighting company in the country runs, because the
  // crew is idle in September and buried in November.
  tiers: [
    { month: 8, offPct: 15 },
    { month: 9, offPct: 10 },
    { month: 10, offPct: 0 },
  ],
  showLadder: true,
  discountLabel: '',
  deposit: { mode: 'none', value: 0, paidAt: null, method: '' },
  dueOn: 'completion',
  dueDate: '',
  payments: [],
  termsLeft:
    'Pricing includes installation, removal, and season-long maintenance. If anything goes out we come back and fix it, no charge.',
  termsRight:
    'The balance is due the day the install is completed. If anything is not right, tell us before you pay it.',
  customerPhone: '',
})

/** For the price column heading, which is 1.15in wide and not negotiable. */
export const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
]

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/* ---------------------------------------------------------------- money --- */

const c = (n: number) => Math.round((Number.isFinite(n) ? n : 0) * 100)
const d = (cents: number) => cents / 100

/** How much comes off, as a fraction, for the month this paper is priced at. */
export function offFraction(p: Paper): number {
  if (p.installMonth == null) return 0
  const t = p.tiers.find((x) => x.month === p.installMonth)
  return t ? Math.min(100, Math.max(0, t.offPct)) / 100 : 0
}

/** The list price of one line, before any seasonal discount. */
export const listCents = (l: QuoteLines[number]) => c(lineTotal(l))

/** What one line is actually being charged at. Rounded here, once. */
export const priceCents = (l: QuoteLines[number], off: number) =>
  Math.round(listCents(l) * (1 - off))

/** Everything at list. */
export function subtotal(lines: QuoteLines): number {
  let t = 0
  for (const l of lines) t += listCents(l)
  return d(t)
}

/** What the job costs if it is installed in a given month. The ladder uses this. */
export function totalAt(lines: QuoteLines, offPct: number): number {
  const off = Math.min(100, Math.max(0, offPct)) / 100
  let t = 0
  for (const l of lines) t += priceCents(l, off)
  return d(t)
}

/** The sum of the right-hand column, and never anything else. */
export function total(lines: QuoteLines, p: Paper): number {
  const off = offFraction(p)
  let t = 0
  for (const l of lines) t += priceCents(l, off)
  return d(t)
}

/**
 * The discount line. Derived by subtraction, so the arithmetic on the page is
 * closed: subtotal minus this equals the total, always, whatever rounding did.
 */
export function discount(lines: QuoteLines, p: Paper): number {
  return d(c(subtotal(lines)) - c(total(lines, p)))
}

/** What the deposit comes to, whether or not any of it has been taken. */
export function depositAmount(lines: QuoteLines, p: Paper): number {
  if (p.deposit.mode === 'none') return 0
  const t = c(total(lines, p))
  const v = Number.isFinite(p.deposit.value) ? p.deposit.value : 0
  const raw = p.deposit.mode === 'percent' ? Math.round((t * v) / 100) : c(v)
  // Never more than the job, and never negative. A deposit larger than the
  // total is a typo every time.
  return d(Math.min(t, Math.max(0, raw)))
}

/**
 * WHAT HAS ACTUALLY BEEN RECEIVED. Not what was asked for.
 *
 * Asking for a deposit and being handed one are different events, and the old
 * model could not tell them apart: setting a deposit percentage and ticking a
 * box marked it paid. So this reads the payment list and nothing else, with
 * one exception - a document written before the list existed, whose single
 * deposit date is folded in here so its balance does not jump back up to the
 * full total the first time somebody opens it. normalizePaper turns that into
 * a real Payment on load; this is the belt to that pair of braces.
 */
export function paidAmount(lines: QuoteLines, p: Paper): number {
  const list = Array.isArray(p.payments) ? p.payments : []
  if (list.length) {
    let t = 0
    for (const q of list) t += c(q.amount)
    // Never more than the job. An overpayment is a typo or a refund, and both
    // are conversations rather than a negative balance on a customer's page.
    return d(Math.min(c(total(lines, p)), Math.max(0, t)))
  }
  return p.deposit.paidAt ? depositAmount(lines, p) : 0
}

/**
 * THE DAY THE LAST MONEY LANDED, or nothing.
 *
 * Latest by date rather than last in the array: payments are recorded when
 * somebody gets round to it, so a check that arrived in November can be
 * entered after one that arrived in December.
 */
export function lastPayment(p: Paper): string {
  const list = Array.isArray(p.payments) ? p.payments : []
  let best = ''
  for (const q of list) {
    const d = (q.date ?? '').slice(0, 10)
    if (d && d > best) best = d
  }
  // A document written before payments were a list carries its one date here.
  if (!best && p.deposit.paidAt) best = p.deposit.paidAt.slice(0, 10)
  return best
}

/** What is still owed. The whole total until money actually arrives. */
export function balanceDue(lines: QuoteLines, p: Paper): number {
  return d(c(total(lines, p)) - c(paidAmount(lines, p)))
}

/**
 * EVERY PAPER THAT COMES OFF DISK, IN ONE SHAPE.
 *
 * Documents saved before deposits could be none, before the balance had its
 * own timing and before payments were a list are missing three fields, and a
 * missing field here is a total that reads as NaN on somebody's invoice. The
 * legacy single deposit becomes the first Payment, once, so the history is
 * true rather than empty.
 *
 * Idempotent, because it runs on every load.
 */
export function normalizePaper(raw: Partial<Paper> | undefined, lines: QuoteLines = []): Paper {
  const base = defaultPaper()
  const p: Paper = {
    ...base,
    ...(raw ?? {}),
    deposit: { ...base.deposit, ...(raw?.deposit ?? {}) },
    tiers: Array.isArray(raw?.tiers) ? raw.tiers : base.tiers,
    payments: Array.isArray(raw?.payments) ? raw.payments : [],
    dueOn: raw?.dueOn === 'date' ? 'date' : 'completion',
    dueDate: typeof raw?.dueDate === 'string' ? raw.dueDate : '',
  }
  if (!p.payments.length && p.deposit.paidAt) {
    const amount = depositAmount(lines, p)
    if (amount > 0) {
      p.payments = [
        {
          id: `legacy-${p.deposit.paidAt.slice(0, 10)}`,
          amount,
          date: p.deposit.paidAt.slice(0, 10),
          method: p.deposit.method || '',
          note: 'Deposit',
        },
      ]
    }
  }
  return p
}

/**
 * What the paper says about when the rest is due.
 *
 * Never invents a date. A document set to a date with no date on it says the
 * honest thing rather than printing an empty line, because the one place this
 * appears is under a number somebody is being asked to pay.
 */
export function dueWording(p: Paper): string {
  if (p.dueOn === 'date') {
    const when = longDate(p.dueDate)
    return when ? `Due ${when}` : 'Due date not set'
  }
  return 'Due on completion'
}

/* --------------------------------------------------------------- format --- */

/** What the discount line is called. The month, unless somebody said otherwise. */
export function discountLabel(p: Paper): string {
  const own = p.discountLabel.trim()
  if (own) return own
  if (p.installMonth == null) return 'Discount'
  return `${MONTHS[p.installMonth]} early booking discount`
}

/** GHL-1068. Falls back to the bare number when no prefix has been set. */
export function docNumber(p: Paper, prefix: string): string {
  const n = p.number.trim()
  const pre = prefix.trim().toUpperCase()
  if (!n) return ''
  return pre ? `${pre}-${n}` : n
}

/** Whole dollars where they are whole, cents where they are not. */
export function money(n: number): string {
  const whole = Math.round(n * 100) % 100 === 0
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  })
}

/** September 6, 2026. Parsed as a plain date, not a timestamp in a timezone. */
export function longDate(iso: string): string {
  const [y, m, day] = iso.split('-').map((x) => parseInt(x, 10))
  if (!y || !m || !day) return ''
  return new Date(y, m - 1, day).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}
